// src/v2/leases/leaseDrawerFinancials.js
//
// Task #9096 — pure read-time helpers for the Lease record drawer Financials
// tab. Never invents payments: annual-only years get no monthly split, missing
// years stay null (not zero), invalid monthly evidence is surfaced as an error.
import { monthlyPayableEvidence, actualPayableMonth } from "../../../shared/monthlyPayable.js";
import { convertAmount } from "../../utils/fxConvert.js";

export { LEASE_DRAWER_TABS } from "../../services/buildUrl.js";
export const LEASE_UNAVAILABLE_COPY = "This lease isn't available. It may have been archived or the link is wrong.";
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const num = (v) => (v == null || v === "" || !Number.isFinite(Number(v)) ? null : Number(v));
const cents = (v) => Math.round((v + Number.EPSILON) * 100) / 100;

/** Year payable amount: monthly-actual rows use net, annual-source rows use net (contractual - abatement). */
export function rowAmount(row) {
  if (!row || row.isNotInSource || row.basis === "unresolved") return null;
  return num(row.net) ?? num(row.contractual);
}

export function hasAbatementSubline(row) {
  return rowAmount(row) != null && num(row.abatement) != null && num(row.abatement) > 0 && num(row.contractual) != null;
}

/** "{Market} · {alias} · {premises}" with no invented segments. */
export function leaseIdentityLine(lease) {
  const market = lease?.market || lease?.city || null;
  const alias = lease?.locationAlias && lease.locationAlias !== market ? lease.locationAlias : null;
  const premises = String(lease?.premises || lease?.suite || "").split(",")[0].trim() || null;
  return [market, alias, premises].filter(Boolean).join(" · ");
}

/** Visible drawer tabs from the DB-backed lease tab flags (same source as the full reader). */
export function drawerTabsFromFlags(leaseTabs) {
  const map = { lease_tab_overview: "overview", lease_tab_financials: "financials", lease_tab_dates: "dates" };
  if (!Array.isArray(leaseTabs) || leaseTabs.length === 0) return [];
  return leaseTabs.filter((t) => t.enabled && map[t.key])
    .sort((a, b) => a.sort_order - b.sort_order).map((t) => map[t.key])
    // LEASE-DRAWER-V2: Terms is a UI split of the authorized Overview flag
    // (no lease_tab_terms flag exists); it follows Overview's enablement.
    .flatMap((k) => (k === "overview" ? ["overview", "terms"] : [k]));
}

/**
 * Month-by-month rows for a schedule year. Only monthly_actual years produce
 * months; annual-only years return { kind: "annual_only" }.
 */
export function monthlyRowsForYear(lease, row, today = new Date()) {
  if (!row) return { kind: "none", months: [] };
  if (row.basis === "unresolved") return { kind: "invalid", issue: row.issue || "Monthly payable evidence could not be loaded", months: [] };
  if (row.isNotInSource) return { kind: "not_in_source", months: [] };
  if (row.basis !== "monthly_actual") return { kind: "annual_only", months: [] };
  const evidence = monthlyPayableEvidence(lease);
  if (evidence.state === "invalid") return { kind: "invalid", issue: evidence.issue, months: [] };
  const year = Number(row.year);
  const curY = today.getFullYear(); const curM = today.getMonth() + 1;
  let ytd = 0;
  const months = [];
  for (let m = 1; m <= 12; m += 1) {
    const p = actualPayableMonth(evidence, year, m, row.currency);
    if (p.basis !== "monthly_actual") return { kind: "invalid", issue: p.issue || "Monthly evidence incomplete", months: [] };
    ytd += p.amount;
    const timing = year < curY || (year === curY && m < curM) ? "paid" : year === curY && m === curM ? "now" : "scheduled";
    months.push({
      month: m, label: `${MONTHS[m - 1]} ${year}`, quarter: Math.ceil(m / 3),
      amount: p.sourced ? p.amount : null, sourced: p.sourced, ytd: cents(ytd),
      status: p.sourced ? timing : "missing",
    });
  }
  const total = cents(ytd);
  return { kind: "monthly", months, total, reconciles: num(row.net) != null && Math.abs(total - Number(row.net)) < 0.005 };
}

const isoDay = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/** Estimate rows (fallback / projected / approximate) are qualified, never source-exact. */
export function isEstimateRow(row) {
  return Boolean(row && (row.isFallback || row.isProjected || row.approximate || row.isEstimate));
}

function convertTo(amount, from, to, rates) {
  if (from === to) return amount;
  const c = convertAmount(amount, from, to, rates);
  return c != null && Number.isFinite(c) ? c : null;
}

/**
 * Remaining obligation from today (date-only, local) through expiry, in
 * `toCcy` (default lease currency). Each contribution converts from its own
 * row currency; a missing rate is a disclosed gap, never a native fallback.
 * Counts only dated source evidence:
 *  - monthly payable rows whose month starts on/after today and within the
 *    expiry month; a partially elapsed current month is excluded (source has
 *    no payment day) and disclosed; a missing month is a gap.
 *  - annual-only rows only for whole calendar years fully inside the window.
 *    A started year, or a terminal year ending mid-year, is a gap: the source
 *    has no partial-year anchor and nothing is prorated.
 *  - estimate rows (fallback / projected) are gaps, not source.
 * Negative and sourced-zero months are kept. Rounds once after summing.
 */
export function remainingObligation(lease, schedule, today = new Date(), { toCcy = null, rates = null } = {}) {
  const ccy = toCcy || lease?.currency || "USD";
  const end = String(lease?.leaseEnd ?? lease?.endDate ?? "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(end) || end.startsWith("9999")) return { amount: null, currency: ccy, complete: false, partialMonth: false, gaps: [], note: "No fixed expiry" };
  const todayIso = isoDay(today);
  if (end < todayIso) return { amount: 0, currency: ccy, complete: true, partialMonth: false, gaps: [], note: "Expired" };
  const curY = today.getFullYear(); const endY = Number(end.slice(0, 4)); const endMonth = end.slice(0, 7);
  let sum = 0; const gaps = []; let partialMonth = false;
  const add = (amount, from, label) => {
    const c = convertTo(Number(amount), from, ccy, rates);
    if (c == null) { gaps.push(`${label} (no ${from}→${ccy} rate)`); return; }
    sum += c;
  };
  const byYear = new Map((schedule || []).map((r) => [Number(r.year), r]));
  for (let y = curY; y <= endY; y += 1) {
    const row = byYear.get(y);
    if (!row || row.isNotInSource) { gaps.push(String(y)); continue; }
    if (row.basis === "unresolved") { gaps.push(`${y} (unresolved)`); continue; }
    if (isEstimateRow(row)) { gaps.push(`${y} (estimate, not source)`); continue; }
    const from = row.currency || lease?.currency || "USD";
    if (row.basis === "monthly_actual") {
      const mr = monthlyRowsForYear(lease, row, today);
      if (mr.kind !== "monthly") { gaps.push(`${y} (${mr.issue || "monthly unavailable"})`); continue; }
      const ev = monthlyPayableEvidence(lease).years.get(y);
      for (const m of mr.months) {
        const start = `${y}-${String(m.month).padStart(2, "0")}-01`;
        const ym = start.slice(0, 7);
        if (ym > endMonth) break;
        if (start < todayIso) {
          if (ym === todayIso.slice(0, 7)) {
            if (m.sourced) partialMonth = true;
            else gaps.push(`${ym} (no payment recorded)`);
          }
          continue;
        }
        if (!m.sourced) { gaps.push(`${ym} (no payment recorded)`); continue; }
        const src = ev.rows.find((r) => r.month === start);
        add(src.rentLocal, src.currency || from, ym);
      }
      continue;
    }
    if (y === curY && todayIso > `${y}-01-01`) { gaps.push(`${y} (annual only; elapsed share unknown)`); continue; }
    if (y === endY && end !== `${y}-12-31`) { gaps.push(`${y} (annual only; term ends ${end}, no partial-year amount in source)`); continue; }
    const a = rowAmount(row);
    if (a == null) { gaps.push(String(y)); continue; }
    add(a, from, String(y));
  }
  const complete = gaps.length === 0 && !partialMonth;
  const qualifiers = [...(partialMonth ? ["current month excluded: source has no payment day"] : []), ...(gaps.length ? [`not counted: ${gaps.join(", ")}`] : [])];
  return { amount: cents(sum), currency: ccy, complete, partialMonth, gaps, note: complete ? `Today through ${end}` : `Partial · ${qualifiers.join(" · ")}` };
}

/** Full-term sourced total in `toCcy`, converting each row from its own currency. */
export function scheduleTotals(schedule, { toCcy = "USD", rates = null } = {}) {
  let total = 0; let missing = 0; let estimates = 0; const fxMissing = [];
  for (const row of schedule || []) {
    const a = rowAmount(row);
    if (a == null) { missing += 1; continue; }
    if (isEstimateRow(row)) { estimates += 1; continue; }
    const from = row.currency || toCcy;
    const c = convertTo(a, from, toCcy, rates);
    if (c == null) { fxMissing.push(row.year); continue; }
    total += c;
  }
  return { total: cents(total), currency: toCcy, missing, estimates, fxMissing };
}

/** Years of the lease term covered by sourced (non-estimate) schedule rows. */
export function termCoverage(lease, schedule) {
  const s = String(lease?.leaseStart ?? lease?.startDate ?? "").slice(0, 4);
  const e = String(lease?.leaseEnd ?? lease?.endDate ?? "").slice(0, 4);
  const sY = Number(s); const eY = Number(e);
  if (!sY || !eY || e.startsWith("9999") || eY < sY) return { termYears: null, sourced: null, missingYears: [], estimateYears: [] };
  const byYear = new Map((schedule || []).map((r) => [Number(r.year), r]));
  const missingYears = []; const estimateYears = []; let sourced = 0;
  for (let y = sY; y <= eY; y += 1) {
    const r = byYear.get(y);
    if (!r || rowAmount(r) == null) missingYears.push(y);
    else if (isEstimateRow(r)) estimateYears.push(y);
    else sourced += 1;
  }
  return { termYears: eY - sY + 1, sourced, missingYears, estimateYears, from: sY, to: eY };
}
